// HierarchyView.jsx
import React, { useState, useEffect, useMemo } from "react";
import api from "../../api";
import HierarchyEdit from "./HierarchyEdit";
import TabBar from "../utils/TabBar";
import DataTable from "../utils/DataTable";

const HierarchyView = ({ showToast }) => {
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeDept, setActiveDept] = useState("");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await api.get("/userinfo");
        setUsers(response.data);
        if (response.data.length > 0) {
          const depts = response.data[0].department
            ? response.data[0].department.split(",").map((d) => d.trim())
            : ["N/A"];
          setActiveDept(depts[0]);
        }
      } catch (error) {
        console.error("Error fetching users:", error);
        showToast("Error fetching users", "error");
      }
    };
    fetchUsers();
  }, [showToast]);

  const departmentTabs = useMemo(() => {
    const deptsSet = new Set();
    users.forEach((user) => {
      if (user.department) {
        user.department
          .split(",")
          .map((d) => d.trim())
          .forEach((d) => deptsSet.add(d));
      } else {
        deptsSet.add("N/A");
      }
    });
    return Array.from(deptsSet);
  }, [users]);

  const isSalesDept = useMemo(
    () => activeDept.toLowerCase() === "sales",
    [activeDept]
  );


  const teamsByLead = useMemo(() => {
    if (!isSalesDept) return {};
    const deptUsers = users.filter((user) => {
      if (!user.department) return false;
      const depts = user.department.split(",").map((d) => d.trim().toLowerCase());
      return depts.includes(activeDept.toLowerCase());
    });

    const groups = {};
    deptUsers.forEach((user) => {
      const role = user.role ? user.role.toLowerCase() : "";
      if (role === "team lead" || role === "admin") {
        groups[user.id] = { teamLead: user, members: [] };
      }
    });
    deptUsers.forEach((user) => {
      const teamId = user.team ? user.team.toString().trim() : "";
      if (teamId && groups[teamId] && user.id.toString() !== teamId) {
        groups[teamId].members.push(user);
      }
    });
    return groups;
  }, [users, activeDept, isSalesDept]);

  const sortedUsers = useMemo(() => {
    if (isSalesDept) return [];
    const deptUsers = users.filter((user) => {
      if (!user.department) return false;
      const depts = user.department.split(",").map((d) => d.trim().toLowerCase());
      return depts.includes(activeDept.toLowerCase());
    });
    const roleOrder = { admin: 0, "team lead": 1, user: 2 };
    return deptUsers.sort((a, b) => {
      const roleA = roleOrder[a.role?.toLowerCase()] ?? 999;
      const roleB = roleOrder[b.role?.toLowerCase()] ?? 999;
      return roleA - roleB;
    });
  }, [users, activeDept, isSalesDept]);

  const handleEdit = (user) => {
    setSelectedUser(user);
  };

  const handleCloseEdit = () => {
    setSelectedUser(null);
    api.get("/userinfo")
      .then((res) => setUsers(res.data))
      .catch((err) => console.error(err));
  };

  const columns = useMemo(
    () => [
      {
        accessor: "selection",
        massSelection: true,
        width: 12,
        Header: "",
      },
      {
        Header: "Name",
        accessor: "name",
        width: 150,
        Cell: ({ row }) =>
          `${row.original.first_name} ${row.original.last_name}`,
      },
      {
        Header: "Email",
        accessor: "email",
        width: 200,
      },
      {
        Header: "Phone",
        accessor: "phone",
        width: 100,
      },
      {
        Header: "Roles",
        accessor: "role",
        width: 120,
        DropdownOptions: ["admin", "team lead", "user"],
        dropdownBackgroundColor: (value) => {
          switch (value) {
            case "admin":
              return "#3498db";
            case "team lead":
              return "#f1c40f";
            case "user":
              return "#2ecc71";
            default:
              return "#fff";
          }
        },
      },
    ],
    []
  );

  return (
    <div className="hierarchy-view">
      <h2>Hierarchy Setup</h2>
      <TabBar>
        {departmentTabs.map((dept) => (
          <button
            key={dept}
            onClick={() => setActiveDept(dept)}
            className={`tab ${dept === activeDept ? "active" : ""}`}
          >
            {dept}
          </button>
        ))}
      </TabBar>
      <div className="department-content" style={{ padding: "10px" }}>
        {isSalesDept ? (
          Object.keys(teamsByLead).length === 0 ? (
            <p>No team lead records found for this department.</p>
          ) : (
            Object.values(teamsByLead).map(({ teamLead, members }) => {
              const tableData = [teamLead, ...members];
              return (
                <div key={teamLead.id} style={{ marginBottom: "20px" }}>
                  <h4>{`${teamLead.first_name} ${teamLead.last_name}'s Team`}</h4>
                  <DataTable
                    columns={columns}
                    data={tableData}
                    showToast={showToast}
                    disablePagination={true}
                  />
                </div>
              );
            })
          )
        ) : (
          <DataTable
            columns={columns}
            data={sortedUsers}
            showToast={showToast}
            disablePagination={true}
          />
        )}
      </div>
      {selectedUser && (
        <HierarchyEdit
          user={selectedUser}
          onClose={handleCloseEdit}
          showToast={showToast}
        />
      )}
    </div>
  );
};

export default HierarchyView;
